import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { Header, ProgressBar } from "./components";

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const BackgroundImage = styled.img`
  width: 100%;
  margin-top: 15px;
`;

const CenterContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 18px;
  padding: 0px 24px;
  margin-top: 20px;
  box-sizing: border-box; /* Ensures padding is included in width calculation */
`;

const Address = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #2f313b;
`;

const Coordinates = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #2f313b;
`;
const BottomButtons = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding: 0px 24px;
  margin-top: auto;
  margin-bottom: 20px;
  gap: 16px; /* Ensures 16px gap */
  box-sizing: border-box;
`;

const SkipButton = styled.button`
  background: transparent;
  border: none;
  color: #2f313b;
  font-size: 16px;
  font-weight: 600;
  width: 30%;
  position: relative;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 8px;

  &:hover {
    opacity: 0.8;
  }

  span {
    font-size: 12px;
    font-weight: 400;
    color: #6c757d;
  }
`;

const NextButton = styled.button`
  background: rgb(51, 51, 51);
  color: white;
  width: 50%;
  border: none;
  padding: 10px 50px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 600;
  &:disabled {
    background: #ccc;
    color: #fff;
    cursor: not-allowed;
  }
`;
const CoordinateValue = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: #05a8aa;
  line-height: 24px;
`;
// const ProgressBar = styled.div`
//   width: 100%;
//   height: 4px;
//   background-color: #e0e0e0;
//   margin: 8px 0;

//   &::after {
//     content: "";
//     display: block;
//     width: 10%;
//     height: 100%;
//     background-color: #20564b;
//   }
// `;
const ProgressContainer = styled.div`
  margin-top: 20px;
  width: 90%;
  padding: 0 24px; /* 24px padding on left and right */
`;
const Footer = styled.footer`
  font-size: 12px;
  font-weight: 400;
  color: #6e6f76;
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 24px;
  margin-top: 10px;
  box-sizing: border-box; /* Ensures padding is included in width calculation */
`;

const PinCode = ({ onProceedWithPincode }) => {
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [address, setAddress] = useState("Fetching...");
  const [pinCode, setPinCode] = useState("Fetching...");
  const [isLoading, setIsLoading] = useState(true);
  const [locationError, setLocationError] = useState(false);
  const GOOGLE_MAPS_API_KEY = "AIzaSyDdYtfS2vEKF-QeULzEvfKT6QAUP-aBhB4"; // Replace with your API key
  
  useEffect(() => {
    const fetchLocation = () => {
      if (navigator.geolocation) {
        // Show message while waiting for permission
        setAddress("Waiting for location permission...");
        
        const locationTimeout = setTimeout(() => {
          // If taking too long, allow user to proceed
          if (isLoading) {
            setAddress("Location permission taking too long");
            setPinCode("Unavailable");
            setLocationError(true);
            setIsLoading(false);
          }
        }, 8000);
        
        navigator.geolocation.getCurrentPosition(
          (position) => {
            clearTimeout(locationTimeout);
            const { latitude, longitude } = position.coords;
            setLatitude(latitude.toFixed(8));
            setLongitude(longitude.toFixed(8));
            
            // Fetch the full address using Google Maps Geocoding API
            fetch(
              `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${GOOGLE_MAPS_API_KEY}`
            )
              .then((response) => {
                if (!response.ok) {
                  throw new Error("Failed to fetch address data");
                }
                return response.json();
              })
              .then((data) => {
                if (data.results && data.results.length > 0) {
                  const fullAddress = data.results[0].formatted_address;
                  const postalCode = data.results[0].address_components.find(
                    (component) => component.types.includes("postal_code")
                  );
                  setAddress(fullAddress || "Address unavailable");
                  setPinCode(postalCode ? postalCode.long_name : "Unavailable");
                } else {
                  setAddress("Address not found");
                  setPinCode("Unavailable");
                }
              })
              .catch((error) => {
                console.error("Error fetching address data:", error);
                setAddress("Error fetching address");
                setPinCode("Error fetching PIN code");
                setLocationError(true);
              })
              .finally(() => {
                setIsLoading(false);
              });
          },
          (error) => {
            clearTimeout(locationTimeout);
            console.error("Error getting location:", error);
            
            // More user-friendly error messages
            if (error.code === 1) {
              setAddress("Location permission denied. Please enable in settings.");
            } else if (error.code === 2) {
              setAddress("Location unavailable. Check your connection.");
            } else if (error.code === 3) {
              setAddress("Location request timed out.");
            } else {
              setAddress("Unable to fetch location");
            }
            
            setPinCode("Unable to fetch PIN code");
            setLocationError(true);
            setIsLoading(false);
          },
          {
            enableHighAccuracy: false, // Set to false for faster response on mobile
            timeout: 10000,
            maximumAge: 0
          }
        );
      } else {
        console.error("Geolocation is not supported by this browser.");
        setAddress("Geolocation not supported by your browser");
        setPinCode("Unavailable");
        setLocationError(true);
        setIsLoading(false);
      }
    };
    fetchLocation();
  }, [GOOGLE_MAPS_API_KEY]);
  
  return (
    <Container>
      <ProgressContainer>
        <ProgressBar currentStep={2} totalSteps={7} type="dark" />
      </ProgressContainer>
      <BackgroundImage
        src="/src/assets/images/Rectangle 3464402.png"
        alt="Background"
      />
      <CenterContent>
        <div>Address</div>
        <Address>{address}</Address>
        <Address>{pinCode}</Address>
        <Coordinates>
          {latitude && longitude ? (
            <>
              <CoordinateValue>Latitude: </CoordinateValue>
              {latitude}
              <br />
              <CoordinateValue>Longitude: </CoordinateValue>
              {longitude}
            </>
          ) : locationError ? (
            <span style={{ color: "#ff6b6b" }}>Location data unavailable</span>
          ) : (
            <span>Fetching coordinates...</span>
          )}
        </Coordinates>
      </CenterContent>
      <Footer>
        The address and PIN Code will be fetched based on the latitude and
        longitude of the user.
      </Footer>
      <BottomButtons className="flex w-full justify-end">
        <SkipButton onClick={() => onProceedWithPincode()}>
          Skip
          <span>For demo use</span>
        </SkipButton>
        <NextButton 
          onClick={() => onProceedWithPincode()} 
          disabled={isLoading && !locationError}
        >
          Next
        </NextButton>
      </BottomButtons>
    </Container>
  );
};

export default PinCode;
